<template>
  <div class="square-terminal-connection-manager">
    <!-- Connection Status Icon -->
    <div
      class="connection-icon"
      :class="connectionStatusClass"
      :title="connectionStatusText"
      data-cy="terminal-connection-status"
      @click="openDeviceModal"
    >
      <!-- Simple circle - no icon needed -->
    </div>

    <!-- Device Selection Modal -->
    <b-modal
      id="terminal-device-selection-modal"
      title="Terminal Configuration"
      size="lg"
      hide-footer
      static
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      @shown="onModalShown"
      @hidden="resetModal"
      @before-hide="validateModalClose"
    >
      <template #modal-header="{ close }">
        <div class="modal-header-custom">
          <h4 class="modal-title">
            Terminal Configuration
          </h4>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="close-button"
            :disabled="!canCloseModal"
            @click="close()"
          >
            <font-awesome-icon icon="times" />
          </b-button>
        </div>
      </template>

      <!-- Tab Navigation -->
      <b-tabs
        :key="modalKey"
        v-model="activeTab"
        pills
        card
        class="terminal-config-tabs"
      >
        <!-- Location Selection Tab -->
        <b-tab :disabled="loading">
          <template #title>
            <font-awesome-icon icon="location-dot" />
            Location
            <span
              v-if="selectedLocationId"
              class="tab-indicator selected"
            >
              <font-awesome-icon icon="check-circle" />
            </span>
            <span
              v-else
              class="tab-indicator required"
            >
              <font-awesome-icon icon="circle-exclamation" />
            </span>
          </template>

          <!-- Loading State -->
          <div
            v-if="loading || loadingLocations"
            class="loading-section"
          >
            <loading-spinner>
              <div slot="title">
                <span v-if="loadingLocations">Refreshing locations...</span>
                <span v-else>Loading available locations...</span>
              </div>
            </loading-spinner>
          </div>

          <!-- Location List -->
          <div
            v-else-if="availableLocations.length > 0"
            class="location-selection-section"
          >
            <div class="info-section">
              <font-awesome-icon
                icon="info-circle"
                class="info-icon"
              />
              <div class="info-content">
                <p>Select a location for your terminal devices. This is required for processing payments.</p>
                <div class="refresh-section">
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    :disabled="loadingLocations"
                    @click="refreshLocations"
                  >
                    <font-awesome-icon
                      :icon="loadingLocations ? 'spinner' : 'arrow-rotate-right'"
                      :spin="loadingLocations"
                    />
                    Refresh Locations
                  </b-button>
                </div>
              </div>
            </div>

            <div class="location-list">
              <div
                v-for="location in availableLocations"
                :key="location.id"
                class="location-item"
                :class="{ 'selected': location.id === tempSelectedLocationId }"
                @click="selectLocation(location)"
              >
                <div class="location-info">
                  <div class="location-header">
                    <div class="location-name">
                      {{ location.name || 'Unknown Location' }}
                    </div>
                    <div class="location-status-badge">
                      <span
                        class="status-badge"
                        :class="getLocationStatusClass(location)"
                      >
                        <font-awesome-icon
                          :icon="getLocationStatusIcon(location)"
                          fixed-width
                        />
                        {{ getLocationStatusText(location) }}
                      </span>
                    </div>
                  </div>

                  <div class="location-details">
                    <div class="detail-section">
                      <div class="detail-label">
                        Location ID
                      </div>
                      <div class="detail-value location-id">
                        {{ location.id }}
                      </div>
                    </div>

                    <div
                      v-if="location.description"
                      class="detail-section"
                    >
                      <div class="detail-label">
                        Description
                      </div>
                      <div class="detail-value">
                        {{ location.description }}
                      </div>
                    </div>

                    <div
                      v-if="getLocationAddress(location)"
                      class="detail-section"
                    >
                      <div class="detail-label">
                        Address
                      </div>
                      <div class="detail-value">
                        {{ getLocationAddress(location) }}
                      </div>
                    </div>

                    <div class="detail-section">
                      <div class="detail-label">
                        Type
                      </div>
                      <div class="detail-value">
                        {{ location.type || 'PHYSICAL' }}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="location.id === tempSelectedLocationId"
                  class="selected-indicator"
                >
                  <font-awesome-icon
                    icon="check-circle"
                    class="selected-icon"
                  />
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <b-button
                variant="primary"
                :disabled="!tempSelectedLocationId"
                @click="confirmLocationSelection"
              >
                Select Location
              </b-button>
              <b-button
                variant="secondary"
                @click="clearLocationSelection"
              >
                Clear Selection
              </b-button>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-else-if="!loading && !loadingLocations"
            class="empty-state"
          >
            <div class="empty-content">
              <div class="empty-icon">
                <font-awesome-icon
                  icon="location-dot"
                  size="3x"
                  class="icon"
                />
              </div>
              <div class="empty-message">
                <h5>No Locations Available</h5>
                <p>
                  There are no locations available for selection.
                  Please ensure your Square account has at least one location configured.
                </p>
                <div class="refresh-section">
                  <b-button
                    variant="primary"
                    :disabled="loadingLocations"
                    @click="refreshLocations"
                  >
                    <font-awesome-icon
                      :icon="loadingLocations ? 'spinner' : 'arrow-rotate-right'"
                      :spin="loadingLocations"
                    />
                    Refresh Locations
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Device Selection Tab -->
        <b-tab :disabled="loading">
          <template #title>
            <font-awesome-icon icon="terminal" />
            Device
            <span
              v-if="selectedDeviceId"
              class="tab-indicator selected"
            >
              <font-awesome-icon icon="check-circle" />
            </span>
            <span
              v-else-if="!selectedLocationId"
              class="tab-indicator disabled"
            >
              <font-awesome-icon icon="ban" />
            </span>
          </template>

          <!-- Loading State -->
          <div
            v-if="loading || loadingDevices"
            class="loading-section"
          >
            <loading-spinner>
              <div slot="title">
                <span v-if="loadingDevices">Refreshing device list...</span>
                <span v-else>Loading available terminal devices...</span>
              </div>
            </loading-spinner>
          </div>

          <!-- Location Required Notice -->
          <div
            v-else-if="!selectedLocationId"
            class="location-required-notice"
          >
            <div class="notice-content">
              <div class="notice-icon">
                <font-awesome-icon
                  icon="info-circle"
                  size="2x"
                  class="icon"
                />
              </div>
              <div class="notice-message">
                <h5>Location Required</h5>
                <p>
                  Please select a location first before choosing a terminal device.
                  Terminal devices are associated with specific locations.
                </p>
                <b-button
                  variant="primary"
                  size="sm"
                  @click="activeTab = 0"
                >
                  <font-awesome-icon icon="location-dot" />
                  Select Location
                </b-button>
              </div>
            </div>
          </div>

          <!-- Device List -->
          <div
            v-else-if="availableDevices.length > 0"
            class="device-selection-section"
          >
            <div class="info-section">
              <font-awesome-icon
                icon="info-circle"
                class="info-icon"
              />
              <div class="info-content">
                <p>Select an available terminal device to use for processing payments. Only devices that are online or available are shown.</p>
              </div>
            </div>

            <div class="device-list">
              <div
                v-for="device in availableDevices"
                :key="device.id"
                class="device-item"
                :class="{ 'selected': device.device_id === tempSelectedDeviceId }"
                @click="selectDevice(device)"
              >
                <div class="device-info">
                  <div class="device-header">
                    <div class="device-name">
                      {{ device.name || 'Unknown Device' }}
                    </div>
                    <div class="device-status-badge">
                      <span
                        class="status-badge"
                        :class="getDeviceStatusClass(device)"
                      >
                        <font-awesome-icon
                          :icon="getDeviceStatusIcon(device)"
                          fixed-width
                        />
                        {{ getDeviceStatusText(device) }}
                      </span>
                    </div>
                  </div>

                  <div class="device-details-flexbox">
                    <!-- Device ID and Location -->
                    <div class="detail-section">
                      <div class="detail-label">
                        Device ID
                      </div>
                      <div class="detail-value device-id">
                        {{ device.device_id }}
                      </div>
                    </div>

                    <div class="detail-section">
                      <div class="detail-label">
                        Location
                      </div>
                      <div class="detail-value">
                        {{ getDeviceLocationName(device) }}
                      </div>
                    </div>

                    <!-- Device Serial Number (if available) -->
                    <div
                      v-if="getDeviceSerial(device)"
                      class="detail-section"
                    >
                      <div class="detail-label">
                        Serial Number
                      </div>
                      <div class="detail-value device-serial">
                        {{ getDeviceSerial(device) }}
                      </div>
                    </div>

                    <!-- Device Model and Version (if available) -->
                    <div
                      v-if="getDeviceModel(device)"
                      class="detail-section"
                    >
                      <div class="detail-label">
                        Device Model
                      </div>
                      <div class="detail-value">
                        {{ getDeviceModel(device) }}
                      </div>
                    </div>

                    <div
                      v-if="getDeviceVersion(device)"
                      class="detail-section"
                    >
                      <div class="detail-label">
                        Firmware Version
                      </div>
                      <div class="detail-value">
                        {{ getDeviceVersion(device) }}
                      </div>
                    </div>

                    <!-- Terminal App Information (if available) -->
                    <div
                      v-if="getTerminalAppInfo(device)"
                      class="detail-section terminal-app-section"
                    >
                      <div class="detail-label">
                        Terminal App
                      </div>
                      <div class="detail-value">
                        <font-awesome-icon
                          icon="mobile-alt"
                          class="app-icon"
                          fixed-width
                        />
                        {{ getTerminalAppInfo(device).name || 'Square Terminal' }}
                      </div>
                      <div
                        v-if="getTerminalAppInfo(device).version"
                        class="detail-info"
                      >
                        Version {{ getTerminalAppInfo(device).version }}
                      </div>
                    </div>

                    <!-- Battery Status (if available) -->
                    <div
                      v-if="getBatteryInfo(device)"
                      class="detail-section battery-section"
                    >
                      <div class="detail-label">
                        Battery
                      </div>
                      <div class="detail-value">
                        <font-awesome-icon
                          :icon="getBatteryIcon(device)"
                          :class="getBatteryClass(device)"
                          fixed-width
                        />
                        {{ getBatteryInfo(device).visible_percent }}%
                      </div>
                      <div class="detail-info">
                        {{ getBatteryStatusText(device) }}
                      </div>
                    </div>

                    <!-- Network Status (if available) -->
                    <div
                      v-if="getNetworkInfo(device)"
                      class="detail-section network-section"
                    >
                      <div class="detail-label">
                        Network
                      </div>
                      <div class="detail-value">
                        <font-awesome-icon
                          :icon="getNetworkIcon(device)"
                          :class="getNetworkClass(device)"
                          fixed-width
                        />
                        {{ getNetworkType(device) }}
                      </div>
                      <div
                        v-if="getNetworkDetails(device)"
                        class="detail-info"
                      >
                        {{ getNetworkDetails(device) }}
                      </div>
                    </div>
                  </div>

                  <!-- Legacy status badge for paired status -->
                  <div class="legacy-status">
                    <span class="status-badge status-paired">
                      <font-awesome-icon
                        icon="circle-check"
                        fixed-width
                      />
                      Paired
                    </span>
                  </div>
                </div>
                <div
                  v-if="device.id === tempSelectedDeviceId"
                  class="selected-indicator"
                >
                  <font-awesome-icon
                    icon="check-circle"
                    class="selected-icon"
                  />
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <b-button
                variant="primary"
                :disabled="!tempSelectedDeviceId"
                @click="confirmSelection"
              >
                Select Device
              </b-button>
              <b-button
                variant="secondary"
                @click="clearSelection"
              >
                Clear Selection
              </b-button>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-else-if="!loading && selectedLocationId"
            class="empty-state"
          >
            <div class="empty-content">
              <div class="empty-icon">
                <font-awesome-icon
                  icon="terminal"
                  size="3x"
                  class="icon"
                />
              </div>
              <div class="empty-message">
                <slot
                  name="empty-message"
                  :close-modal="closeModal"
                >
                  <h5>No Terminal Devices Available</h5>
                  <p>
                    There are no terminal devices available for selection.
                    Please ensure your terminal devices are online and connected.
                  </p>
                  <a
                    href="#"
                    class="btn-link-styled"
                    @click.prevent="goToDeviceCodes"
                  >
                    Manage Device Codes
                  </a>
                </slot>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <!-- Modal Footer with Status -->
      <div class="modal-footer-status">
        <div class="status-summary">
          <div class="status-item">
            <font-awesome-icon icon="location-dot" />
            <span class="status-label">Location:</span>
            <span class="status-value">
              {{ selectedLocationName || 'Not selected' }}
            </span>
          </div>
          <div class="status-item">
            <font-awesome-icon icon="terminal" />
            <span class="status-label">Device:</span>
            <span class="status-value">
              {{ selectedDeviceName || 'Not selected' }}
            </span>
          </div>
        </div>
        <div class="footer-actions">
          <b-button
            variant="secondary"
            :disabled="!canCloseModal"
            @click="closeModal"
          >
            <font-awesome-icon
              v-if="!canCloseModal"
              icon="exclamation-triangle"
            />
            {{ canCloseModal ? 'Close' : 'Location Required' }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'square-terminal-connection-manager',
  data: function() {
    return {
      loading: false,
      loadingLocations: false,
      loadingDevices: false,
      tempSelectedDeviceId: null,
      tempSelectedLocationId: null,
      activeTab: 1, // 0 = Location, 1 = Device - Start with Device tab
      modalKey: 0 // Key to force tab re-rendering
    }
  },
  computed: {
    // Get all terminal device codes from store
    terminalDeviceCodes: function() {
      return this.$store.state.square.terminalDeviceCodes || []
    },

    // Get all terminal devices from store
    terminalDevices: function() {
      return this.$store.state.square.terminalDevices || []
    },

    // Get all locations from store
    locations: function() {
      return this.$store.state.square.locations || []
    },

    // Filter only active locations
    availableLocations: function() {
      return this.locations.filter(location =>
        location.status === 'ACTIVE' || location.status === 'active'
      )
    },

    // Get currently selected location ID from store
    selectedLocationId: function() {
      return this.$store.state.square.location || null
    },

    // Get selected location object
    selectedLocation: function() {
      if (!this.selectedLocationId) return null
      return this.availableLocations.find(location => location.id === this.selectedLocationId)
    },

    // Get selected location name for display
    selectedLocationName: function() {
      return this.selectedLocation ? this.selectedLocation.name : null
    },

    // Get selected device name for display
    selectedDeviceName: function() {
      return this.selectedDevice ? this.selectedDevice.name : null
    },

    // Check if modal can be closed (location must be selected)
    canCloseModal: function() {
      return !!this.selectedLocationId
    },

    // Filter only paired device codes and merge with device information
    availableDevices: function() {
      return this.terminalDeviceCodes
        .filter(deviceCode => deviceCode.status === 'PAIRED')
        .map(deviceCode => {
          // Find the corresponding terminal device
          // Device codes have device_id without prefix, terminal devices have "device:<device_id>"
          const matchingDevice = this.terminalDevices.find(device => {
            const deviceId = device.id.replace(/^device:/, '')
            return deviceId === deviceCode.device_id
          })

          // Merge device code information with terminal device details
          return {
            ...deviceCode,
            terminalDevice: matchingDevice,
            // Use terminal device info for status, battery, network if available
            status: matchingDevice?.status || { category: 'UNKNOWN' },
            components: matchingDevice?.components || [],
            attributes: matchingDevice?.attributes || {},
            // Keep device code info for selection
            id: deviceCode.id,
            name: deviceCode.name,
            device_id: deviceCode.device_id
          }
        })
    },

    // Get currently selected device ID from store
    selectedDeviceId: function() {
      return this.$store.state.square.selectedTerminalDeviceId || null
    },

    // Get selected device object
    selectedDevice: function() {
      if (!this.selectedDeviceId) return null
      return this.availableDevices.find(device => device.device_id === this.selectedDeviceId)
    },

    // Connection status for styling
    connectionStatusClass: function() {
      if (this.selectedDevice) {
        return 'status-connected'
      }
      else if (this.availableDevices.length > 0) {
        return 'status-available'
      }
      else {
        return 'status-no-devices'
      }
    },

    // Connection status text for tooltip
    connectionStatusText: function() {
      if (this.selectedDevice) {
        return `Connected to: ${this.selectedDevice.name || 'Unknown Device'}`
      }
      else if (this.availableDevices.length > 0) {
        return `${this.availableDevices.length} device(s) available - Click to select`
      }
      else {
        return 'No terminal devices available'
      }
    }
  },
  mounted: function() {
    // Load initial data first
    this.$store.dispatch('square/getIsConnected', { force: true })
      .then(isConnected => {
        if (isConnected) {
          return this.loadDeviceDataAndValidateSelection()
        }
        else {
          console.warn('Square terminal is not connected')
          // Still try to load device data and validate selection
          return this.loadDeviceDataAndValidateSelection()
        }
      })
      .catch(error => {
        console.error('Error checking Square connection status:', error)
        // Still try to load device data and validate selection
        return this.loadDeviceDataAndValidateSelection()
      })
  },
  methods: {
    // Load device data and validate stored selection
    loadDeviceDataAndValidateSelection: function() {
      this.loading = true
      return Promise.all([
        this.$store.dispatch('square/loadDevicesList', { force: false }),
        this.$store.dispatch('square/loadDeviceCodesList', { force: false })
      ])
        .then(() => {
          // Load location first
          if (!this.selectedLocationId) {
            this.loadSelectedLocationFromStorage()
          }

          // Then handle device selection
          if (!this.selectedDeviceId) {
            // No selected device in store, try to load from localStorage
            this.loadSelectedDeviceFromStorage()
          }
          else {
            // There's already a selected device, validate it still exists and is paired
            this.validateCurrentSelection()
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    // Validate the currently selected device still exists and is paired
    validateCurrentSelection: function() {
      const currentDeviceId = this.selectedDeviceId
      if (currentDeviceId) {
        this.$nextTick(() => {
          const device = this.availableDevices.find(d => d.id === currentDeviceId)

          if (!device) {
            // Currently selected device no longer exists or is not paired
            console.warn(`Currently selected device ${currentDeviceId} is no longer available. Clearing selection.`)
            this.$store.commit('square/SET_SELECTED_TERMINAL_DEVICE', null)
            this.removeSelectedDeviceFromStorage()
            this.$emit('device-cleared')
          }
          else {
            console.log(`Validated currently selected device: ${device.name || currentDeviceId}`)
          }
        })
      }
    },

    // Load device data
    loadDeviceData: function() {
      this.loading = true
      Promise.all([
        this.$store.dispatch('square/loadDevicesList', { force: false }),
        this.$store.dispatch('square/loadDeviceCodesList', { force: false }),
        this.$store.dispatch('square/loadLocations', { force: false })
      ])
        .finally(() => {
          this.loading = false
        })
    },

    // Open device selection modal
    // Open device selection modal
    openDeviceModal: function() {
      // Set temp selections to current selections
      this.tempSelectedDeviceId = this.selectedDeviceId
      this.tempSelectedLocationId = this.selectedLocationId

      // Force tabs to re-render by incrementing key
      this.modalKey++

      // Reset activeTab to force fresh state
      this.activeTab = 0

      // Show modal first
      this.$bvModal.show('terminal-device-selection-modal')

      // Set device tab after a brief delay
      this.$nextTick(() => {
        setTimeout(() => {
          this.activeTab = 1
        }, 50)
      })

      // Refresh device data when opening modal with minimum display time
      this.loadDevicesWithDelay()
    },

    // Handle modal shown event
    onModalShown: function() {
      // Ensure device tab is selected when modal is fully shown and interactive
      // Force reset to location tab first, then switch to device tab
      this.activeTab = 0
      setTimeout(() => {
        this.activeTab = 1
      }, 100)
    },

    // Refresh locations list
    refreshLocations: function() {
      this.loadingLocations = true

      // Create a minimum delay promise of 3 seconds
      const minimumDelay = new Promise(resolve => setTimeout(resolve, 3000))

      // Execute both the API call and minimum delay
      Promise.all([
        this.$store.dispatch('square/loadLocations', { force: true }),
        minimumDelay
      ])
        .finally(() => {
          this.loadingLocations = false
        })
    },

    // Load devices with minimum display time
    loadDevicesWithDelay: function() {
      this.loadingDevices = true

      // Create a minimum delay promise of 2 seconds
      const minimumDelay = new Promise(resolve => setTimeout(resolve, 2000))

      // Execute both the API calls and minimum delay
      Promise.all([
        this.$store.dispatch('square/loadDevicesList', { force: true }),
        this.$store.dispatch('square/loadDeviceCodesList', { force: true }),
        minimumDelay
      ])
        .finally(() => {
          this.loadingDevices = false
        })
    },

    // Validate modal close - prevent closing if no location selected
    validateModalClose: function(event) {
      if (!this.canCloseModal) {
        event.preventDefault()
        this.$bvToast.toast('Please select a location before closing.', {
          title: 'Location Required',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000
        })
        return false
      }
      return true
    },

    // Location selection methods
    selectLocation: function(location) {
      this.tempSelectedLocationId = location.id
    },

    confirmLocationSelection: function() {
      if (this.tempSelectedLocationId) {
        this.$store.commit('square/SET_LOCATION', this.tempSelectedLocationId)
        this.saveSelectedLocationToStorage(this.tempSelectedLocationId)
        this.$emit('location-selected', this.tempSelectedLocationId)

        // Switch to device tab after location selection
        this.activeTab = 1
      }
    },

    clearLocationSelection: function() {
      this.$store.commit('square/SET_LOCATION', null)
      this.removeSelectedLocationFromStorage()
      this.tempSelectedLocationId = null
      this.$emit('location-cleared')
    },

    // Close modal method that can be called from slot content
    closeModal: function() {
      if (this.canCloseModal) {
        this.$bvModal.hide('terminal-device-selection-modal')
      }
    },

    // Select a device (temporary selection)
    selectDevice: function(device) {
      this.tempSelectedDeviceId = device.device_id
    },

    // Confirm device selection
    confirmSelection: function() {
      if (this.tempSelectedDeviceId) {
        this.$store.commit('square/SET_SELECTED_TERMINAL_DEVICE', this.tempSelectedDeviceId)
        this.saveSelectedDeviceToStorage(this.tempSelectedDeviceId)
        this.$emit('device-selected', this.tempSelectedDeviceId)
      }
      this.$bvModal.hide('terminal-device-selection-modal')
    },

    // Clear device selection
    clearSelection: function() {
      this.$store.commit('square/SET_SELECTED_TERMINAL_DEVICE', null)
      this.removeSelectedDeviceFromStorage()
      this.tempSelectedDeviceId = null // Clear the temporary selection in the modal
      this.$emit('device-cleared')
      // Don't close the modal - just clear the selection
    },

    // Reset modal state
    resetModal: function() {
      this.tempSelectedDeviceId = null
      this.tempSelectedLocationId = null
      // Don't set activeTab here - let openDeviceModal control it
    },

    // Get device location name
    getDeviceLocationName: function(device) {
      if (!device.location_id) return 'Unknown Location'

      const location = this.$store.state.square.locations?.find(loc => loc.id === device.location_id)
      return location ? (location.display_name || location.name) : 'Unknown Location'
    },

    // Navigate to device codes management
    goToDeviceCodes: function() {
      this.$bvModal.hide('terminal-device-selection-modal')
      // Emit event to parent to handle navigation
      this.$emit('navigate-to-device-codes')
    },

    // Save selected location to localStorage
    saveSelectedLocationToStorage: function(locationId) {
      try {
        localStorage.setItem('square-selected-location', locationId)
      }
      catch (error) {
        console.warn('Failed to save selected location to localStorage:', error)
      }
    },

    // Load selected location from localStorage
    loadSelectedLocationFromStorage: function() {
      try {
        const savedLocationId = localStorage.getItem('square-selected-location')
        if (savedLocationId) {
          console.log(`Attempting to restore location from localStorage: ${savedLocationId}`)

          this.$nextTick(() => {
            const location = this.availableLocations.find(l => l.id === savedLocationId)

            if (location) {
              this.$store.commit('square/SET_LOCATION', savedLocationId)
              console.log(`Restored selected location from localStorage: ${location.name || savedLocationId}`)
              this.$emit('location-selected', savedLocationId)
            }
            else {
              console.warn(`Saved location ${savedLocationId} is no longer available`)
              this.removeSelectedLocationFromStorage()
              this.$store.commit('square/SET_LOCATION', null)
              this.$emit('location-cleared')
            }
          })
        }
      }
      catch (error) {
        console.warn('Failed to load selected location from localStorage:', error)
      }
    },

    // Remove selected location from localStorage
    removeSelectedLocationFromStorage: function() {
      try {
        localStorage.removeItem('square-selected-location')
      }
      catch (error) {
        console.warn('Failed to remove selected location from localStorage:', error)
      }
    },

    // Location utility methods
    getLocationStatusClass: function(location) {
      const status = location.status?.toLowerCase() || 'unknown'
      switch (status) {
      case 'active': return 'status-active'
      case 'inactive': return 'status-inactive'
      default: return 'status-unknown'
      }
    },

    getLocationStatusIcon: function(location) {
      const status = location.status?.toLowerCase() || 'unknown'
      switch (status) {
      case 'active': return 'circle-check'
      case 'inactive': return 'circle-xmark'
      default: return 'circle-question'
      }
    },

    getLocationStatusText: function(location) {
      const status = location.status?.toLowerCase() || 'unknown'
      switch (status) {
      case 'active': return 'Active'
      case 'inactive': return 'Inactive'
      default: return 'Unknown'
      }
    },

    getLocationAddress: function(location) {
      if (!location.address) return null

      const parts = []
      if (location.address.address_line_1) parts.push(location.address.address_line_1)
      if (location.address.address_line_2) parts.push(location.address.address_line_2)
      if (location.address.locality) parts.push(location.address.locality)
      if (location.address.administrative_district_level_1) parts.push(location.address.administrative_district_level_1)
      if (location.address.postal_code) parts.push(location.address.postal_code)

      return parts.length > 0 ? parts.join(', ') : null
    },

    // Save selected device to localStorage
    saveSelectedDeviceToStorage: function(deviceId) {
      try {
        localStorage.setItem('square-selected-terminal-device', deviceId)
      }
      catch (error) {
        console.warn('Failed to save selected device to localStorage:', error)
      }
    },

    // Load selected device from localStorage
    loadSelectedDeviceFromStorage: function() {
      try {
        const savedDeviceId = localStorage.getItem('square-selected-terminal-device')
        if (savedDeviceId) {
          // console.log(`Attempting to restore device from localStorage: ${savedDeviceId}`)

          // Wait for next tick to ensure computed properties are updated
          this.$nextTick(() => {
            // Check if the device exists in available devices (which are already filtered for PAIRED status)
            const device = this.availableDevices.find(d => d.device_id === savedDeviceId)

            // console.log('Available devices:', this.availableDevices.map(d => ({ id: d.device_id, name: d.name, status: d.status })))
            // console.log('Found device:', device)

            if (device) {
              // Device exists and is available (already filtered for PAIRED), set it in the store
              this.$store.commit('square/SET_SELECTED_TERMINAL_DEVICE', savedDeviceId)
              console.log(`Restored selected terminal device from localStorage: ${device.name || savedDeviceId}`)
              this.$emit('device-selected', savedDeviceId)
            }
            else {
              // Device no longer exists or is not available
              console.warn(`Saved device ${savedDeviceId} is no longer available. Available device IDs: ${this.availableDevices.map(d => d.device_id).join(', ')}`)
              this.removeSelectedDeviceFromStorage()
              this.$store.commit('square/SET_SELECTED_TERMINAL_DEVICE', null)
              this.$emit('device-cleared')
            }
          })
        }
        else {
          console.log('No saved device found in localStorage')
        }
      }
      catch (error) {
        console.warn('Failed to load selected device from localStorage:', error)
      }
    },

    // Remove selected device from localStorage
    removeSelectedDeviceFromStorage: function() {
      try {
        localStorage.removeItem('square-selected-terminal-device')
      }
      catch (error) {
        console.warn('Failed to remove selected device from localStorage:', error)
      }
    },

    // Device information extraction methods
    getDeviceStatusClass: function(device) {
      // For device codes, we check if they have actual device status
      const deviceStatus = device.status?.category || device.device_status
      switch (deviceStatus) {
      case 'OFFLINE': return 'status-offline'
      case 'ONLINE': return 'status-online'
      case 'NEEDS_ATTENTION': return 'status-warning'
      default: return 'status-paired' // Default for paired device codes
      }
    },

    getDeviceStatusIcon: function(device) {
      const deviceStatus = device.status?.category || device.device_status
      switch (deviceStatus) {
      case 'OFFLINE': return 'circle-xmark'
      case 'ONLINE': return 'circle-check'
      case 'NEEDS_ATTENTION': return 'triangle-exclamation'
      default: return 'circle-check' // Default for paired device codes
      }
    },

    getDeviceStatusText: function(device) {
      const deviceStatus = device.status?.category || device.device_status
      switch (deviceStatus) {
      case 'OFFLINE': return 'Offline'
      case 'ONLINE': return 'Online'
      case 'NEEDS_ATTENTION': return 'Needs Attention'
      default: return 'Connected' // Default for paired device codes
      }
    },

    getDeviceModel: function(device) {
      const manufacturer = device.attributes?.manufacturer || ''
      const model = device.attributes?.model || ''
      return manufacturer && model ? `${manufacturer} ${model}` : null
    },

    getDeviceVersion: function(device) {
      return device.attributes?.version || null
    },

    // Get device serial number
    getDeviceSerial: function(device) {
      return device.attributes?.serial_number || device.attributes?.serialNumber || null
    },

    // Get terminal app information
    getTerminalAppInfo: function(device) {
      const components = this.getComponents(device)
      const appComponent = components.find(c => c.type === 'APPLICATION' || c.type === 'TERMINAL_APP')

      if (appComponent?.application_details) {
        return {
          name: appComponent.application_details.application_name || 'Square Terminal',
          version: appComponent.application_details.application_version
        }
      }

      // Fallback to device attributes if no app component found
      if (device.attributes?.app_name || device.attributes?.app_version) {
        return {
          name: device.attributes.app_name || 'Square Terminal',
          version: device.attributes.app_version
        }
      }

      // If we have any terminal device data, assume it has the app
      if (device.terminalDevice) {
        return {
          name: 'Square Terminal',
          version: null
        }
      }

      return null
    },

    // Component information getters
    getComponents: function(device) {
      return device.components || []
    },

    getBatteryInfo: function(device) {
      const components = this.getComponents(device)
      const batteryComponent = components.find(c => c.type === 'BATTERY')
      return batteryComponent?.battery_details || null
    },

    getWifiInfo: function(device) {
      const components = this.getComponents(device)
      const wifiComponent = components.find(c => c.type === 'WIFI')
      return wifiComponent?.wifi_details || null
    },

    getEthernetInfo: function(device) {
      const components = this.getComponents(device)
      const ethernetComponent = components.find(c => c.type === 'ETHERNET')
      return ethernetComponent?.ethernet_details || null
    },

    getNetworkInfo: function(device) {
      return this.getWifiInfo(device) || this.getEthernetInfo(device)
    },

    // Battery status methods
    getBatteryIcon: function(device) {
      const batteryInfo = this.getBatteryInfo(device)
      if (!batteryInfo) return 'battery-empty'
      const percent = batteryInfo.visible_percent
      if (percent >= 75) return 'battery-full'
      if (percent >= 50) return 'battery-three-quarters'
      if (percent >= 25) return 'battery-half'
      if (percent >= 10) return 'battery-quarter'
      return 'battery-empty'
    },

    getBatteryClass: function(device) {
      const batteryInfo = this.getBatteryInfo(device)
      if (!batteryInfo) return 'battery-unknown'
      const percent = batteryInfo.visible_percent
      if (percent <= 20) return 'battery-low'
      if (percent <= 50) return 'battery-medium'
      return 'battery-good'
    },

    getBatteryStatusText: function(device) {
      const batteryInfo = this.getBatteryInfo(device)
      if (!batteryInfo) return 'Unknown'
      const externalPower = batteryInfo.external_power
      if (externalPower === 'AVAILABLE_CHARGING') return 'Charging'
      if (externalPower === 'AVAILABLE_NOT_CHARGING') return 'Plugged In'
      return 'On Battery'
    },

    // Network status methods
    getNetworkType: function(device) {
      const wifiInfo = this.getWifiInfo(device)
      const ethernetInfo = this.getEthernetInfo(device)
      if (wifiInfo?.active) return 'Wi-Fi'
      if (ethernetInfo?.active) return 'Ethernet'
      return 'Disconnected'
    },

    getNetworkIcon: function(device) {
      const wifiInfo = this.getWifiInfo(device)
      const ethernetInfo = this.getEthernetInfo(device)
      if (wifiInfo?.active) return 'wifi'
      if (ethernetInfo?.active) return 'ethernet'
      return 'xmark'
    },

    getNetworkClass: function(device) {
      const wifiInfo = this.getWifiInfo(device)
      const ethernetInfo = this.getEthernetInfo(device)
      if (wifiInfo?.active || ethernetInfo?.active) return 'network-connected'
      return 'network-disconnected'
    },

    getNetworkDetails: function(device) {
      const wifiInfo = this.getWifiInfo(device)
      if (wifiInfo?.active) {
        const signalStrength = this.getSignalStrengthText(wifiInfo)
        return `${wifiInfo.ssid} (${signalStrength})`
      }
      const ethernetInfo = this.getEthernetInfo(device)
      if (ethernetInfo?.active) {
        return 'Ethernet Connected'
      }
      return null
    },

    getSignalStrengthText: function(wifiInfo) {
      if (!wifiInfo?.signal_strength) return 'Unknown Signal'
      const strength = wifiInfo.signal_strength.value
      if (strength >= 0.8) return 'Excellent Signal'
      if (strength >= 0.6) return 'Good Signal'
      if (strength >= 0.4) return 'Fair Signal'
      if (strength >= 0.2) return 'Poor Signal'
      return 'Very Poor Signal'
    }
  }
}
</script>

<style lang="scss" scoped>
// Use high specificity to override Bootstrap and global styles
.square-terminal-connection-manager {
  display: inline-flex !important;
  align-items: center !important;

  .connection-icon {
    width: 18px !important;
    height: 18px !important;
    border-radius: 50% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    border: 2px solid transparent !important;

    &:hover {
      transform: scale(1.1) !important;
      border-color: rgba(255, 255, 255, 0.3) !important;
    }

    &.status-connected {
      background-color: #28a745 !important;
      box-shadow: 0 0 8px rgba(40, 167, 69, 0.4) !important;
    }

    &.status-available {
      background-color: #dc3545 !important;
      box-shadow: 0 0 8px rgba(220, 53, 69, 0.4) !important;
    }

    &.status-no-devices {
      background-color: #6f42c1 !important;
      box-shadow: 0 0 8px rgba(111, 66, 193, 0.4) !important;
    }
  }

  // Modal styles - now scoped since modal is static/inline
  ::v-deep .modal {
    // Only target the backdrop/overlay, not the modal content
    &::before {
      background-color: rgba(0, 0, 0, 0.5) !important;
      opacity: 1 !important;
    }

    .modal-dialog {
      // Ensure modal dialog itself is fully opaque and styled properly
      opacity: 1 !important;

      .modal-content {
        background-color: #ffffff !important;
        border: 1px solid #dee2e6 !important;
        border-radius: 0.375rem !important;
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        opacity: 1 !important;

        .modal-header-custom {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          width: 100% !important;
          padding: 1rem 1.5rem !important;
          border-bottom: 1px solid #dee2e6 !important;
          margin: 0 !important;
          text-align: left !important;

          .modal-title {
            margin: 0 !important;
            font-size: 1.25rem !important;
            color: #333 !important;
            flex: 1 !important;
            text-align: left !important;
          }

          .close-button {
            padding: 0.375rem 0.75rem !important;
            border-radius: 0.25rem !important;
            transition: all 0.2s ease !important;
            margin-left: auto !important;
            flex-shrink: 0 !important;
            background-color: #007bff !important;
            border: 1px solid #007bff !important;
            color: #ffffff !important;

            &:hover {
              background-color: #0056b3 !important;
              border-color: #0056b3 !important;
              color: #ffffff !important;
              box-shadow: 0 2px 8px rgba(0, 123, 255, 0.25) !important;
              transform: translateY(-1px) !important;
            }

            &:focus {
              outline: none !important;
              box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5) !important;
            }

            &:active {
              background-color: #004085 !important;
              border-color: #004085 !important;
              color: #ffffff !important;
              transform: translateY(0) !important;
              box-shadow: 0 1px 4px rgba(0, 123, 255, 0.25) !important;
            }
          }
        }

        .modal-body {
          background-color: #ffffff !important;
          opacity: 1 !important;
          padding: 0 !important; // Remove padding since tabs handle spacing

          .terminal-config-tabs {
            .nav-pills {
              .nav-link {
                display: flex !important;
                align-items: center !important;
                gap: 0.5rem !important;
                font-weight: 500 !important;

                .tab-indicator {
                  margin-left: 0.25rem !important;

                  &.selected {
                    color: #28a745 !important;
                  }

                  &.required {
                    color: #dc3545 !important;
                  }

                  &.disabled {
                    color: #6c757d !important;
                  }
                }
              }

              .nav-item {
                .nav-link.disabled {
                  opacity: 0.5 !important;
                  cursor: not-allowed !important;
                }
              }
            }

            .tab-content {
              padding: 1.5rem !important;
              background-color: #ffffff !important;
            }
          }

          .location-required-notice {
            padding: 2rem !important;
            text-align: center !important;

            .notice-content {
              .notice-icon {
                margin-bottom: 1rem !important;

                .icon {
                  color: #007bff !important;
                  opacity: 0.8 !important;
                }
              }

              .notice-message {
                h5 {
                  color: #333 !important;
                  margin-bottom: 1rem !important;
                }

                p {
                  color: #6c757d !important;
                  line-height: 1.6 !important;
                  margin-bottom: 1.5rem !important;
                }
              }
            }
          }

          .location-selection-section {
            .location-list {
              margin-bottom: 1.5rem !important;

              .location-item {
                border: 1px solid #e0e0e0 !important;
                border-radius: 8px !important;
                padding: 16px !important;
                margin-bottom: 0.75rem !important;
                cursor: pointer !important;
                transition: all 0.2s ease !important;
                background-color: #ffffff !important;
                display: flex !important;
                flex-direction: column !important;
                position: relative !important;
                text-align: left !important;

                &:hover {
                  border-color: #007bff !important;
                  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15) !important;
                  transform: translateY(-1px) !important;
                }

                &.selected {
                  border-color: #007bff !important;
                  background-color: #e7f3ff !important;
                  box-shadow: 0 2px 12px rgba(0, 123, 255, 0.2) !important;
                }

                .location-info {
                  flex: 1 !important;
                  text-align: left !important;

                  .location-header {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: flex-start !important;
                    margin-bottom: 16px !important;
                    text-align: left !important;

                    .location-name {
                      font-weight: bold !important;
                      font-size: 1.2rem !important;
                      color: #333 !important;
                      flex: 1 !important;
                      line-height: 1.3 !important;
                      text-align: left !important;
                    }

                    .location-status-badge {
                      flex-shrink: 0 !important;
                      margin-left: 12px !important;

                      .status-badge {
                        display: flex !important;
                        align-items: center !important;
                        gap: 6px !important;
                        padding: 4px 8px !important;
                        border-radius: 12px !important;
                        font-size: 0.75rem !important;
                        font-weight: 600 !important;
                        text-transform: uppercase !important;
                        letter-spacing: 0.5px !important;

                        &.status-active {
                          background-color: #d4edda !important;
                          color: #155724 !important;
                          border: 1px solid #c3e6cb !important;
                        }

                        &.status-inactive {
                          background-color: #f8d7da !important;
                          color: #721c24 !important;
                          border: 1px solid #f5c6cb !important;
                        }

                        &.status-unknown {
                          background-color: #f8f9fa !important;
                          color: #6c757d !important;
                          border: 1px solid #dee2e6 !important;
                        }
                      }
                    }
                  }

                  .location-details {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    gap: 12px !important;
                    margin-bottom: 16px !important;
                    width: 100% !important;
                    text-align: left !important;

                    .detail-section {
                      display: flex !important;
                      flex-direction: column !important;
                      min-width: 140px !important;
                      flex: 1 1 140px !important;
                      max-width: calc(50% - 6px) !important;
                      background-color: transparent !important;
                      padding: 0 !important;
                      text-align: left !important;
                      align-items: flex-start !important;

                      .detail-label {
                        font-size: 0.75rem !important;
                        font-weight: 600 !important;
                        color: #6c757d !important;
                        text-transform: uppercase !important;
                        letter-spacing: 0.5px !important;
                        margin-bottom: 4px !important;
                        text-align: left !important;
                      }

                      .detail-value {
                        font-weight: 600 !important;
                        color: #333 !important;
                        font-size: 0.875rem !important;
                        display: flex !important;
                        align-items: center !important;
                        gap: 6px !important;
                        margin-bottom: 2px !important;
                        word-break: break-word !important;
                        text-align: left !important;
                        justify-content: flex-start !important;

                        &.location-id {
                          font-family: 'Courier New', monospace !important;
                          font-size: 0.8rem !important;
                          color: #007bff !important;
                          letter-spacing: 1px !important;
                        }
                      }
                    }
                  }
                }

                .selected-indicator {
                  position: absolute !important;
                  top: 12px !important;
                  right: 12px !important;
                  background-color: #007bff !important;
                  color: white !important;
                  border-radius: 50% !important;
                  width: 28px !important;
                  height: 28px !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3) !important;

                  .selected-icon {
                    font-size: 1rem !important;
                  }
                }
              }
            }
          }

          .modal-footer-status {
            border-top: 1px solid #e9ecef !important;
            padding: 1rem 1.5rem !important;
            background-color: #f8f9fa !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            width: 100% !important;
            flex-wrap: wrap !important;
            gap: 1rem !important;

            .status-summary {
              display: flex !important;
              gap: 1.5rem !important;
              flex: 1 !important;
              align-items: center !important;

              .status-item {
                display: flex !important;
                align-items: center !important;
                gap: 0.5rem !important;
                font-size: 0.875rem !important;

                .status-label {
                  font-weight: 600 !important;
                  color: #6c757d !important;
                }

                .status-value {
                  color: #333 !important;
                  font-weight: 500 !important;
                }
              }
            }

            .footer-actions {
              display: flex !important;
              gap: 0.75rem !important;
              align-items: center !important;
              flex-shrink: 0 !important;

              .btn {
                min-width: 120px !important;
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
                border-radius: 0.375rem !important;
                transition: all 0.2s ease !important;
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
                gap: 0.5rem !important;
                text-decoration: none !important;
                vertical-align: middle !important;
                border: 1px solid transparent !important;
                line-height: 1.5 !important;
                font-weight: 500 !important;

                &.btn-secondary {
                  background-color: #6c757d !important;
                  border-color: #6c757d !important;
                  color: #ffffff !important;

                  &:hover:not(:disabled) {
                    background-color: #5a6268 !important;
                    border-color: #545b62 !important;
                    color: #ffffff !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                  }

                  &:focus {
                    outline: none !important;
                    box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5) !important;
                  }

                  &:disabled {
                    background-color: #6c757d !important;
                    border-color: #6c757d !important;
                    opacity: 0.65 !important;
                    cursor: not-allowed !important;
                  }
                }

                &.btn-primary {
                  background-color: #007bff !important;
                  border-color: #007bff !important;
                  color: #ffffff !important;

                  &:hover:not(:disabled) {
                    background-color: #0056b3 !important;
                    border-color: #0056b3 !important;
                    color: #ffffff !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.25) !important;
                  }

                  &:focus {
                    outline: none !important;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5) !important;
                  }

                  &:disabled {
                    background-color: #007bff !important;
                    border-color: #007bff !important;
                    opacity: 0.65 !important;
                    cursor: not-allowed !important;
                  }
                }
              }
            }

            // Responsive adjustments
            @media (max-width: 576px) {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 1rem !important;

              .status-summary {
                width: 100% !important;
                flex-direction: column !important;
                align-items: flex-start !important;
                gap: 0.5rem !important;
              }

              .footer-actions {
                width: 100% !important;
                justify-content: flex-end !important;
              }
            }
          }

          .loading-section {
            padding: 2rem !important;
            text-align: center !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 200px !important;

            // Center the loading spinner component
            .loading-spinner {
              display: flex !important;
              flex-direction: column !important;
              align-items: center !important;
              justify-content: center !important;
              width: 100% !important;
            }
          }

          .info-section {
            display: flex !important;
            align-items: flex-start !important;
            padding: 1rem !important;
            background-color: #e7f3ff !important;
            border: 1px solid #b8daff !important;
            border-radius: 6px !important;
            margin-bottom: 1.5rem !important;

            .info-icon {
              color: #0066cc !important;
              font-size: 1.25rem !important;
              margin-right: 0.75rem !important;
              margin-top: 0.125rem !important;
              flex-shrink: 0 !important;
            }

            .info-content {
              flex: 1 !important;
              text-align: left !important;

              p {
                margin: 0 !important;
                line-height: 1.5 !important;
                color: #004085 !important;
                text-align: left !important;
              }
            }
          }

          .device-list {
            margin-bottom: 1.5rem !important;

            .device-item {
              border: 1px solid #e0e0e0 !important;
              border-radius: 8px !important;
              padding: 16px !important;
              margin-bottom: 0.75rem !important;
              cursor: pointer !important;
              transition: all 0.2s ease !important;
              background-color: #ffffff !important;
              display: flex !important;
              flex-direction: column !important;
              position: relative !important;
              text-align: left !important;

              &:hover {
                border-color: #007bff !important;
                box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15) !important;
                transform: translateY(-1px) !important;
              }

              &.selected {
                border-color: #007bff !important;
                background-color: #e7f3ff !important;
                box-shadow: 0 2px 12px rgba(0, 123, 255, 0.2) !important;
              }

              .device-info {
                flex: 1 !important;
                text-align: left !important;

                .device-header {
                  display: flex !important;
                  justify-content: space-between !important;
                  align-items: flex-start !important;
                  margin-bottom: 16px !important;
                  text-align: left !important;

                  .device-name {
                    font-weight: bold !important;
                    font-size: 1.2rem !important;
                    color: #333 !important;
                    flex: 1 !important;
                    line-height: 1.3 !important;
                    text-align: left !important;
                  }

                  .device-status-badge {
                    flex-shrink: 0 !important;
                    margin-left: 12px !important;

                    .status-badge {
                      display: flex !important;
                      align-items: center !important;
                      gap: 6px !important;
                      padding: 4px 8px !important;
                      border-radius: 12px !important;
                      font-size: 0.75rem !important;
                      font-weight: 600 !important;
                      text-transform: uppercase !important;
                      letter-spacing: 0.5px !important;

                      &.status-online {
                        background-color: #d4edda !important;
                        color: #155724 !important;
                        border: 1px solid #c3e6cb !important;
                      }

                      &.status-offline {
                        background-color: #f8d7da !important;
                        color: #721c24 !important;
                        border: 1px solid #f5c6cb !important;
                      }

                      &.status-warning {
                        background-color: #fff3cd !important;
                        color: #856404 !important;
                        border: 1px solid #ffeaa7 !important;
                      }

                      &.status-paired {
                        background-color: #d1ecf1 !important;
                        color: #0c5460 !important;
                        border: 1px solid #bee5eb !important;
                      }

                      &.status-unknown {
                        background-color: #f8f9fa !important;
                        color: #6c757d !important;
                        border: 1px solid #dee2e6 !important;
                      }
                    }
                  }
                }

                .device-details-flexbox {
                  display: flex !important;
                  flex-wrap: wrap !important;
                  gap: 12px !important;
                  margin-bottom: 16px !important;
                  width: 100% !important;
                  text-align: left !important;

                  .detail-section {
                    display: flex !important;
                    flex-direction: column !important;
                    min-width: 140px !important;
                    flex: 1 1 140px !important;
                    max-width: calc(50% - 6px) !important;
                    background-color: transparent !important;
                    padding: 0 !important;
                    text-align: left !important;
                    align-items: flex-start !important;

                    .detail-label {
                      font-size: 0.75rem !important;
                      font-weight: 600 !important;
                      color: #6c757d !important;
                      text-transform: uppercase !important;
                      letter-spacing: 0.5px !important;
                      margin-bottom: 4px !important;
                      text-align: left !important;
                    }

                    .detail-value {
                      font-weight: 600 !important;
                      color: #333 !important;
                      font-size: 0.875rem !important;
                      display: flex !important;
                      align-items: center !important;
                      gap: 6px !important;
                      margin-bottom: 2px !important;
                      word-break: break-word !important;
                      text-align: left !important;
                      justify-content: flex-start !important;

                      &.device-id {
                        font-family: 'Courier New', monospace !important;
                        font-size: 0.8rem !important;
                        color: #007bff !important;
                        letter-spacing: 1px !important;
                      }

                      &.device-serial {
                        font-family: 'Courier New', monospace !important;
                        font-size: 0.8rem !important;
                        color: #6c757d !important;
                        letter-spacing: 0.5px !important;
                      }
                    }

                    .detail-info {
                      font-size: 0.75rem !important;
                      color: #6c757d !important;
                      line-height: 1.2 !important;
                      text-align: left !important;
                    }

                    &.terminal-app-section {
                      .detail-value {
                        .app-icon {
                          color: #007bff !important;
                        }
                      }
                    }

                    &.battery-section {
                      .detail-value {
                        .battery-good {
                          color: #28a745 !important;
                        }

                        .battery-medium {
                          color: #ffc107 !important;
                        }

                        .battery-low {
                          color: #dc3545 !important;
                        }

                        .battery-unknown {
                          color: #6c757d !important;
                        }
                      }
                    }

                    &.network-section {
                      .detail-value {
                        .network-connected {
                          color: #28a745 !important;
                        }

                        .network-disconnected {
                          color: #dc3545 !important;
                        }
                      }
                    }

                    @media (max-width: 768px) {
                      flex: 1 1 100% !important;
                      max-width: 100% !important;
                    }
                  }
                }

                .legacy-status {
                  margin-top: 0.5rem !important;
                  text-align: left !important;

                  .status-badge {
                    display: inline-flex !important;
                    align-items: center !important;
                    gap: 0.375rem !important;
                    padding: 0.25rem 0.5rem !important;
                    border-radius: 12px !important;
                    font-size: 0.75rem !important;
                    font-weight: 600 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.5px !important;

                    &.status-paired {
                      background-color: #d4edda !important;
                      color: #155724 !important;
                      border: 1px solid #c3e6cb !important;
                    }
                  }
                }
              }

              .selected-indicator {
                position: absolute !important;
                top: 12px !important;
                right: 12px !important;
                background-color: #007bff !important;
                color: white !important;
                border-radius: 50% !important;
                width: 28px !important;
                height: 28px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3) !important;

                .selected-icon {
                  font-size: 1rem !important;
                }
              }

              // Responsive adjustments
              @media (max-width: 576px) {
                .device-info {
                  .device-details-flexbox {
                    flex-direction: column !important;
                    align-items: flex-start;
                    gap: 8px;
                  }

                  .device-header {
                    flex-direction: column !important;
                    align-items: flex-start;
                    gap: 8px;

                    .device-status-badge {
                      margin-left: 0;
                    }
                  }
                }
              }
            }
          }

          // Modal footer comprehensive styling
          .modal-footer-status {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
            width: 100% !important;
            gap: 1rem !important;
            padding: 1.5rem !important;
            border-top: 1px solid #e0e0e0 !important;
            background-color: #f8f9fa !important;
            text-align: left !important;

            .status-summary {
              display: flex !important;
              flex-direction: row !important;
              gap: 2rem !important;
              margin-bottom: 1rem !important;
              flex-wrap: wrap !important;
              align-items: center !important;

              .status-item {
                display: flex !important;
                align-items: center !important;
                gap: 0.5rem !important;
                font-size: 0.9rem !important;
                color: #495057 !important;

                .status-label {
                  font-weight: 600 !important;
                  color: #6c757d !important;
                }

                .status-value {
                  color: #333 !important;
                  font-weight: 500 !important;
                }

                svg {
                  color: #6c757d !important;
                  width: 14px !important;
                  height: 14px !important;
                }
              }
            }

            .footer-actions {
              display: flex !important;
              flex-direction: row !important;
              justify-content: flex-end !important;
              align-items: center !important;
              gap: 0.75rem !important;
              width: 100% !important;
              flex-wrap: wrap !important;

              .btn {
                min-width: 120px !important;
                padding: 0.5rem 1rem !important;
                font-weight: 600 !important;
                border-radius: 6px !important;
                transition: all 0.2s ease !important;
                border: 2px solid transparent !important;
                white-space: nowrap !important;
                text-align: center !important;

                &.btn-primary {
                  background-color: #007bff !important;
                  color: white !important;
                  border-color: #007bff !important;

                  &:hover:not(:disabled) {
                    background-color: #0056b3 !important;
                    border-color: #0056b3 !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.2) !important;
                  }
                }

                &.btn-secondary {
                  background-color: #6c757d !important;
                  color: white !important;
                  border-color: #6c757d !important;

                  &:hover:not(:disabled) {
                    background-color: #545b62 !important;
                    border-color: #545b62 !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 8px rgba(108, 117, 125, 0.2) !important;
                  }
                }

                &.btn-outline-secondary {
                  background-color: transparent !important;
                  color: #6c757d !important;
                  border-color: #6c757d !important;

                  &:hover:not(:disabled) {
                    background-color: #6c757d !important;
                    color: white !important;
                    border-color: #6c757d !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 8px rgba(108, 117, 125, 0.15) !important;
                  }
                }

                &:disabled {
                  opacity: 0.6 !important;
                  cursor: not-allowed !important;
                  transform: none !important;
                  box-shadow: none !important;
                }

                svg {
                  margin-right: 0.5rem !important;
                }
              }

              // Responsive design for footer actions
              @media (max-width: 768px) {
                flex-direction: column !important;
                align-items: stretch !important;

                .btn {
                  width: 100% !important;
                  min-width: auto !important;
                  margin-bottom: 0.5rem !important;

                  &:last-child {
                    margin-bottom: 0 !important;
                  }
                }
              }
            }
          }

          .modal-actions {
            display: flex !important;
            gap: 1.5rem !important;
            justify-content: flex-end !important;
            padding-top: 1.5rem !important;
            border-top: 1px solid #e9ecef !important;
            align-items: center !important;
            flex-wrap: wrap !important;

            .btn {
              min-width: 120px !important;
              padding: 0.5rem 1rem !important;
              font-size: 0.875rem !important;
              border-radius: 0.375rem !important;
              transition: all 0.2s ease !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
              gap: 0.5rem !important;
              text-decoration: none !important;
              vertical-align: middle !important;
              border: 1px solid transparent !important;
              line-height: 1.5 !important;
              font-weight: 500 !important;

              &.btn-primary {
                background-color: #007bff !important;
                border-color: #007bff !important;
                color: #ffffff !important;

                &:hover:not(:disabled) {
                  background-color: #0056b3 !important;
                  border-color: #0056b3 !important;
                  color: #ffffff !important;
                  transform: translateY(-1px) !important;
                  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.25) !important;
                }

                &:focus {
                  outline: none !important;
                  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5) !important;
                }

                &:disabled {
                  background-color: #007bff !important;
                  border-color: #007bff !important;
                  opacity: 0.65 !important;
                  cursor: not-allowed !important;
                }
              }

              &.btn-secondary {
                background-color: #6c757d !important;
                border-color: #6c757d !important;
                color: #ffffff !important;

                &:hover:not(:disabled) {
                  background-color: #5a6268 !important;
                  border-color: #545b62 !important;
                  color: #ffffff !important;
                  transform: translateY(-1px) !important;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                }

                &:focus {
                  outline: none !important;
                  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5) !important;
                }

                &:disabled {
                  background-color: #6c757d !important;
                  border-color: #6c757d !important;
                  opacity: 0.65 !important;
                  cursor: not-allowed !important;
                }
              }
            }

            // Responsive adjustments
            @media (max-width: 576px) {
              justify-content: center !important;
              gap: 0.75rem !important;

              .btn {
                flex: 1 !important;
                max-width: 200px !important;
              }
            }
          }

          .empty-state {
            padding: 2rem !important;
            text-align: center !important;

            .empty-content {
              .empty-icon {
                margin-bottom: 1.5rem !important;

                .icon {
                  color: #6c757d !important;
                  opacity: 0.6 !important;
                }
              }

              .empty-message {
                h5 {
                  color: #333 !important;
                  margin-bottom: 1rem !important;
                }

                p {
                  color: #6c757d !important;
                  line-height: 1.6 !important;
                  margin-bottom: 1.5rem !important;
                }

                .refresh-section {
                  margin-top: 1rem !important;

                  .btn {
                    .fa-spinner,
                    .fa-arrow-rotate-right {
                      margin-right: 0.375rem !important;
                    }
                  }
                }

                .btn-link-styled {
                  display: inline-block !important;
                  padding: 0.5rem 1rem !important;
                  background-color: #007bff !important;
                  color: white !important;
                  text-decoration: none !important;
                  border-radius: 0.25rem !important;
                  font-weight: 500 !important;
                  transition: all 0.2s ease !important;
                  border: 1px solid #007bff !important;

                  &:hover {
                    background-color: #0056b3 !important;
                    border-color: #0056b3 !important;
                    color: white !important;
                    text-decoration: none !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.25) !important;
                  }

                  &:focus {
                    outline: none !important;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5) !important;
                  }

                  &:active {
                    transform: translateY(0) !important;
                    box-shadow: 0 1px 2px rgba(0, 123, 255, 0.25) !important;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

// Legacy modal styles (in case modal is not static)
.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  margin: 0;

  .modal-title {
    margin: 0;
    font-size: 1.25rem;
    color: #333;
    flex: 1;
  }

  .close-button {
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    transition: background-color 0.2s;
    margin-left: auto;
    flex-shrink: 0;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}

.loading-section {
  padding: 2rem;
  text-align: center;
}

.info-section {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  background-color: #e7f3ff;
  border: 1px solid #b8daff;
  border-radius: 6px;
  margin-bottom: 1.5rem;

  .info-icon {
    color: #0066cc;
    font-size: 1.25rem;
    margin-right: 0.75rem;
    margin-top: 0.125rem;
    flex-shrink: 0;
  }

  .info-content {
    flex: 1;

    p {
      margin: 0;
      line-height: 1.5;
      color: #004085;
    }

    .refresh-section {
      margin-top: 0.75rem;

      .btn {
        font-size: 0.875rem;

        .fa-spinner {
          margin-right: 0.375rem;
        }

        .fa-arrow-rotate-right {
          margin-right: 0.375rem;
        }
      }
    }
  }
}

.device-list {
  margin-bottom: 1.5rem;

  .device-item {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    position: relative;

    &:hover {
      border-color: #007bff;
      box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
      transform: translateY(-1px);
    }

    &.selected {
      border-color: #007bff;
      background-color: #e7f3ff;
      box-shadow: 0 2px 12px rgba(0, 123, 255, 0.2);
    }

    .device-info {
      flex: 1;

      .device-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 16px;

        .device-name {
          font-weight: bold;
          font-size: 1.2rem;
          color: #333;
          flex: 1;
          line-height: 1.3;
        }

        .device-status-badge {
          flex-shrink: 0;
          margin-left: 12px;

          .status-badge {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;

            &.status-online {
              background-color: #d4edda;
              color: #155724;
              border: 1px solid #c3e6cb;
            }

            &.status-offline {
              background-color: #f8d7da;
              color: #721c24;
              border: 1px solid #f5c6cb;
            }

            &.status-warning {
              background-color: #fff3cd;
              color: #856404;
              border: 1px solid #ffeaa7;
            }

            &.status-paired {
              background-color: #d1ecf1;
              color: #0c5460;
              border: 1px solid #bee5eb;
            }

            &.status-unknown {
              background-color: #f8f9fa;
              color: #6c757d;
              border: 1px solid #dee2e6;
            }
          }
        }
      }

      .device-details-flexbox {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 12px !important;
        margin-bottom: 16px !important;
        width: 100% !important;

        .detail-section {
          display: flex !important;
          flex-direction: column !important;
          min-width: 140px !important;
          flex: 1 1 140px !important;
          max-width: calc(50% - 6px) !important;
          background-color: transparent;
          padding: 0;

          .detail-label {
            font-size: 0.75rem;
            font-weight: 600;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 4px;
          }

          .detail-value {
            font-weight: 600;
            color: #333;
            font-size: 0.875rem;
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 2px;
            word-break: break-word;

            &.device-id {
              font-family: 'Courier New', monospace;
              font-size: 0.8rem;
              color: #007bff;
              letter-spacing: 1px;
            }

            &.device-serial {
              font-family: 'Courier New', monospace;
              font-size: 0.8rem;
              color: #6c757d;
              letter-spacing: 0.5px;
            }
          }

          .detail-info {
            font-size: 0.75rem;
            color: #6c757d;
            line-height: 1.2;
          }

          &.terminal-app-section {
            .detail-value {
              .app-icon {
                color: #007bff;
              }
            }
          }

          &.battery-section {
            .detail-value {
              .battery-good {
                color: #28a745;
              }

              .battery-medium {
                color: #ffc107;
              }

              .battery-low {
                color: #dc3545;
              }

              .battery-unknown {
                color: #6c757d;
              }
            }
          }

          &.network-section {
            .detail-value {
              .network-connected {
                color: #28a745;
              }

              .network-disconnected {
                color: #dc3545;
              }
            }
          }

          @media (max-width: 768px) {
            flex: 1 1 100% !important;
            max-width: 100% !important;
          }
        }
      }

      .legacy-status {
        margin-top: 0.5rem;

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;

          &.status-paired {
            background-color: #d4edda;
            color: #155724;
            border: 1px solid #c3e6cb;
          }
        }
      }
    }

    .selected-indicator {
      position: absolute;
      top: 12px;
      right: 12px;
      background-color: #007bff;
      color: white;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);

      .selected-icon {
        font-size: 1rem;
      }
    }

    // Responsive adjustments
    @media (max-width: 576px) {
      .device-info {
        .device-details-flexbox {
          flex-direction: column !important;
          align-items: flex-start;
          gap: 8px;
        }

        .device-header {
          flex-direction: column !important;
          align-items: flex-start;
          gap: 8px;

          .device-status-badge {
            margin-left: 0;
          }
        }
      }
    }
  }
}
</style>

<!-- Scoped global styles to prevent conflicts with main POS CSS -->
<style lang="scss">
// Static modal backdrop styles - Bootstrap Vue handles these differently
// Scope all global styles under a specific selector to avoid conflicts
.square-terminal-connection-manager {
  // Target the modal backdrop when it's rendered as static
  .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.5) !important;
    opacity: 0.5 !important;
  }

  // Target the modal when it has backdrop
  .modal {
    &.show {
      .modal-backdrop {
        background-color: rgba(0, 0, 0, 0.5) !important;
        opacity: 0.5 !important;
      }
    }
  }
}

// For Bootstrap Vue static modals, the backdrop might be handled differently
// Use specific ID selector to avoid global conflicts
#terminal-device-selection-modal {
  &.modal.show {
    .modal-backdrop {
      background-color: rgba(0, 0, 0, 0.5) !important;
      opacity: 0.5 !important;
    }
  }

  .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.5) !important;
    opacity: 0.5 !important;
  }

  &.show .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.5) !important;
    opacity: 0.5 !important;
  }
}

// Override any parent application backdrop styles - but only for our specific modal
.modal#terminal-device-selection-modal {
  .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.5) !important;
    opacity: 0.5 !important;
  }

  &.show .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.5) !important;
    opacity: 0.5 !important;
  }
}

// Bootstrap Vue specific static modal backdrop - scoped to our modal only
.b-modal#terminal-device-selection-modal .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5) !important;
  opacity: 0.5 !important;
}

// Additional fallback for any modal backdrop - but only for our modal
body .modal-backdrop {
  &.terminal-modal-backdrop {
    background-color: rgba(0, 0, 0, 0.5) !important;
    opacity: 0.5 !important;
  }
}

// Ensure Bootstrap modal backdrop works correctly - scoped to our modal
.modal#terminal-device-selection-modal {
  &.fade .modal-dialog {
    transition: transform 0.3s ease-out !important;
  }

  &.show .modal-dialog {
    transform: none !important;
  }
}
</style>
